import { useState, useEffect } from "react";
import { Play, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCoachContext } from "@/contexts/CoachContext";

interface FeaturedVideo {
  id: string;
  youtube_video_id: string;
  title: string;
  description: string | null;
}

const FeaturedVideos = () => {
  const { coachId } = useCoachContext();
  const [videos, setVideos] = useState<FeaturedVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  useEffect(() => {
    fetchVideos();
  }, [coachId]);

  const fetchVideos = async () => {
    let query = supabase.from("featured_videos").select("*").eq("is_active", true).order("display_order").limit(6);
    query = coachId ? query.eq("coach_id", coachId) : query.is("coach_id", null);
    const { data, error } = await query;
    if (!error && data) { setVideos(data); if (data.length > 0) setSelectedVideo(data[0].youtube_video_id); }
    setIsLoading(false);
  };

  if (isLoading || videos.length === 0) return null;

  const currentVideo = videos.find((v) => v.youtube_video_id === selectedVideo);

  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="max-w-[980px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center max-w-[680px] mx-auto mb-12">
          <h2 className="text-4xl md:text-5xl lg:text-[56px] font-semibold text-foreground tracking-[-0.03em] leading-tight mb-4">
            Watch & Learn
          </h2>
          <p className="text-xl text-muted-foreground font-normal">
            Insights, inspiration, and guidance from our coaching sessions.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative aspect-video bg-foreground rounded-2xl overflow-hidden mb-5">
            <iframe
              src={`https://www.youtube.com/embed/${selectedVideo}?rel=0`}
              title={currentVideo?.title || "Featured Video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>

          {currentVideo && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-foreground tracking-tight">{currentVideo.title}</h3>
              {currentVideo.description && <p className="text-sm text-muted-foreground mt-1">{currentVideo.description}</p>}
            </div>
          )}

          {videos.length > 1 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {videos.map((video) => (
                <button
                  key={video.id}
                  onClick={() => setSelectedVideo(video.youtube_video_id)}
                  className={`relative group rounded-xl overflow-hidden transition-all duration-200 ${
                    selectedVideo === video.youtube_video_id ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "hover:ring-2 hover:ring-muted-foreground/30"
                  }`}
                >
                  <img src={`https://img.youtube.com/vi/${video.youtube_video_id}/mqdefault.jpg`} alt={video.title} className="w-full aspect-video object-cover" />
                  <div className="absolute inset-0 bg-foreground/30 group-hover:bg-foreground/10 transition-colors flex items-center justify-center">
                    <div className="w-9 h-9 rounded-full bg-background/90 flex items-center justify-center">
                      <Play className="w-3.5 h-3.5 text-foreground fill-current ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-foreground/80 to-transparent">
                    <p className="text-[11px] text-background font-medium line-clamp-2">{video.title}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 text-center">
            <a href="https://www.youtube.com/@MelinaBenance" target="_blank" rel="noopener noreferrer"
              className="text-foreground text-sm font-normal hover:underline inline-flex items-center gap-1">
              <ExternalLink className="w-3.5 h-3.5" /> Visit YouTube Channel
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedVideos;
